import type { Dayjs } from "dayjs";

import type {
  Appointment,
  AppointmentSlot,
  GetUpcomingIntakeAvailabilityPayload,
  GetUpcomingIntakeAvailabilityResponse,
  PatientDetails,
} from "@/features/calendar/types/schedule";
import dayjs, { guessTimezone } from "@/lib/helpers/dayjs";
import { patientRequests } from "@/api/(requests)/patient";

const AMOUNT_OF_DAYS_IN_WEEK = 7;

const getEarliestAvailableAppointment = (appointments: Appointment[]) =>
  appointments.find((appointment) => appointment.slots.length > 0);

export const getTodaysDate = () => dayjs();

export const findFirstAvailableSlot = (appointments: Appointment[]): Nullable<AppointmentSlot> => {
  const firstAppointmentWithSlots = appointments.find(
    (appointment) => appointment?.slots?.length > 0,
  );

  return firstAppointmentWithSlots?.slots[0] ?? null;
};

export const getAppointmentKey = (appointment: Appointment) =>
  `${appointment.year}-${appointment.month}-${appointment.day}`;

export const getDateFromAppointment = (appointment: Appointment) =>
  dayjs({
    day: appointment.day,
    month: Number(appointment.month) - 1,
    year: appointment.year,
  });

export const adjustAppointmentSlots = (
  slots: AppointmentSlot[],
  maxDaySize: number,
): Nullable<AppointmentSlot[]> =>
  Array.from({ length: maxDaySize }, (_, i) => (i < slots.length ? slots[i] : null));

export const getAppointmentFromDate = (date: Dayjs, slots?: AppointmentSlot[]): Appointment => ({
  day: date.date().toString(),
  day_of_week: date.format("dddd"),
  month: date.format("MM"),
  month_of_year: date.format("MMMM"),
  slots: slots || [],
  year: date.year().toString(),
});

export const generateWeekStructure = (
  startDate: Date,
  appointments: Appointment[],
): Appointment[] => {
  const startOfWeek = dayjs(startDate).startOf("isoWeek");

  return Array.from({ length: AMOUNT_OF_DAYS_IN_WEEK }, (_, index) => {
    const currentDate = startOfWeek.add(index, "day");

    const existingData = appointments.find((appointment) =>
      dayjs(getDateFromAppointment(appointment)).isSame(currentDate),
    );

    return getAppointmentFromDate(currentDate, existingData?.slots);
  });
};

export const getWeekWithFirstAvailableAppointment = (
  appointments: Nullable<Appointment[]>,
  fallbackDate: Date,
) => {
  if (!appointments?.length) return fallbackDate;

  const earliestAvailableAppointment = getEarliestAvailableAppointment(appointments);

  if (!earliestAvailableAppointment) return fallbackDate;

  const appointmentDate = getDateFromAppointment(earliestAvailableAppointment);

  return appointmentDate.startOf("isoWeek").toDate();
};

export const getMoreWeeklyAppointments = async (
  appointments: Appointment[],
  patientDetails: PatientDetails,
  targetWeekStart: Date,
): Promise<Nullable<Appointment[]>> => {
  const patientId = patientDetails?.username;
  const overrideMeetingDuration = patientDetails?.override_meeting_duration;
  const browserTimezone = guessTimezone();
  const weekStart = dayjs(targetWeekStart).startOf("isoWeek");
  const weekEnd = dayjs(targetWeekStart).startOf("isoWeek").add(1, "week");

  const payload: GetUpcomingIntakeAvailabilityPayload = {
    end_iso_timestamp: weekEnd.toISOString(),
    id: patientId,
    start_iso_timestamp: weekStart.toISOString(),
    timezone: browserTimezone,
  };

  if (overrideMeetingDuration != null) {
    payload["override_meeting_duration"] = overrideMeetingDuration;
  }

  try {
    const result = (await patientRequests.get_upcoming_intake_availability({
      data: payload,
      /** TODO: Create a generic type for API responses. Type http util with interceptors */
    })) as undefined as Awaited<GetUpcomingIntakeAvailabilityResponse>;

    const appointmentDays = result?.upcoming_availability || [];

    const existingDays = new Set(appointments.map((day) => getAppointmentKey(day)));
    const newAppointments = appointmentDays.filter(
      (day) => !existingDays.has(getAppointmentKey(day)),
    );

    const updatedAppointments = appointments.concat(newAppointments);

    return updatedAppointments;
  } catch {
    return null;
  }
};

export const hasDataForNextWeek = (appointments: Appointment[], nextWeek: Date) => {
  const nextWeekStart = dayjs(nextWeek).startOf("isoWeek");
  const nextWeekEnd = dayjs(nextWeek).endOf("isoWeek");

  return appointments.some((appointment) => {
    const date = getDateFromAppointment(appointment);

    return date.isSameOrAfter(nextWeekStart) && date.isSameOrBefore(nextWeekEnd);
  });
};
